// ============================================================================
// kepBetolto.js — a csatolt képek HITELESÍTETT betöltése.
//
// A PROBLÉMA (AUDIT 2026-09-26, "A" kockázat): a Firebase Storage
// letöltési URL-je egy hordozható kulcsot tartalmaz. Aki ismeri a címet,
// bejelentkezés NÉLKÜL megnyitja - a Storage-szabályok ilyenkor nem
// érvényesülnek. Ez azt jelenti, hogy
//   - egy eltávolított tag a korábban látott képeket továbbra is megnyithatja,
//   - egy továbbküldött link bárkinek megmutatja a képet.
//
// A MEGOLDÁS: a képet nem a nyilvános URL-lel jelenítjük meg, hanem a
// Storage SDK `getBlob()` hívásával töltjük le. Az a hívás a bejelentkezett
// felhasználó nevében megy, tehát a storage.rules MINDEN megnyitásnál
// érvényesül: aki már nem tagja a szobának, nem kapja meg a képet.
//
// A letöltött adatból `blob:` címet készítünk. Ezt a CSP engedi
// (img-src ... blob:), és a lapon kívül semmit nem jelent - nem
// továbbküldhető, a lap bezárásakor megszűnik.
//
// MIÉRT KELL GYORSÍTÓTÁR: az üzenetlista minden állapotváltozásnál
// újraépül. Gyorsítótár nélkül minden újrarajzolás újra letöltené az
// összes látható képet - felesleges forgalom, és a kép minden alkalommal
// felvillanna. A blob-címeket ezért útvonal szerint megjegyezzük, és
// csak akkor engedjük el őket, ha kiszorulnak a tárból (a fel nem
// szabadított blob a lap memóriájában maradna).
// ============================================================================

import { log } from "./log.js";

// Ennyi kép blob-címét tartjuk egyszerre. A lista 50 üzenetet mutat, ennél
// jóval kevesebb szokott kép lenni; a tartalék a visszagörgetéshez kell.
const MAX_CACHE = 60;

// útvonal -> { url } ; a beszúrási sorrend a Map-ben megmarad, ezt
// használjuk a legrégebbi elem kiszorítására.
const cache = new Map();
// útvonal -> folyamatban lévő letöltés; enélkül ugyanaz a kép egyszerre
// többször is letöltődne (a lista újrarajzolása gyors egymásutánban hívhat).
const folyamatban = new Map();

function elenged(url) {
  try {
    URL.revokeObjectURL(url);
  } catch (e) {
    // A cím felszabadítása sosem akaszthat meg semmit.
  }
}

/** A legrégebbi bejegyzés kiszorítása, a blob-cím felszabadításával. */
function szoritsKi() {
  const elso = cache.keys().next();
  if (elso.done) return;
  const bejegyzes = cache.get(elso.value);
  cache.delete(elso.value);
  if (bejegyzes && bejegyzes.url) elenged(bejegyzes.url);
}

/**
 * Egy kép hitelesített betöltése, gyorsítótárral.
 *
 * TARTALÉK ÚT - VALÓS HIBÁBÓL: a `getBlob()` a háttérben XHR-kérést indít
 * a tárhelyre, ami CORS-engedélyt igényel a Storage-vödrön. Ez NEM
 * alapértelmezett, és a `<img src>` betöltésre nem vonatkozik - ezért a
 * hitelesített letöltésre állás után MINDEN kép eltűnt, amíg a vödrön
 * nem volt beállítva a CORS (lásd docs/UZEMELTETES.md).
 *
 * Ezért: ha a hitelesített letöltés nem megy, visszaesünk a nyilvános
 * letöltési címre. A kép így mindig látszik; a szigorúbb (szabály szerint
 * ellenőrzött) út pedig automatikusan él, amint a CORS be van állítva.
 *
 * @param {string} path a Storage-útvonal (az üzenet `image.path` mezője)
 * @param {string} [fallbackUrl] a nyilvános cím, ha a hitelesített út elbukik
 * @param {object} [opts] csak a teszteknek
 * @param {(path: string) => Promise<Blob>} [opts.letolt] a letöltés módja
 * @returns {Promise<string|null>} a megjeleníthető cím, vagy null
 */
export async function kepCim(path, fallbackUrl, opts = {}) {
  if (typeof path !== "string" || path === "") {
    // Útvonal nélkül (nagyon régi üzenet) csak a nyilvános cím marad.
    return typeof fallbackUrl === "string" && /^https:\/\//i.test(fallbackUrl) ? fallbackUrl : null;
  }

  const meglevo = cache.get(path);
  if (meglevo) return meglevo.url;

  const futo = folyamatban.get(path);
  if (futo) return futo;

  const letolt = opts.letolt || alapLetoltes;
  const igeret = (async () => {
    try {
      const blob = await letolt(path);
      const url = URL.createObjectURL(blob);
      cache.set(path, { url });
      while (cache.size > MAX_CACHE) szoritsKi();
      return url;
    } catch (err) {
      // Két eset lehet:
      //  - a tag már nem tagja a szobának (a szabály elutasítja) -> a
      //    tartalék cím sem fog működni, a kép helyén jelzés marad;
      //  - a vödrön nincs CORS-engedély -> a tartalék címmel a kép
      //    látszik, csak a szabályellenőrzés marad el.
      // Nem tudjuk biztosan megkülönböztetni őket, ezért MINDIG a
      // tartalékot adjuk vissza, ha van: a működő alkalmazás fontosabb.
      log.debug("[kép] hitelesített letöltés nem sikerült:", path, err && err.code);
      if (typeof fallbackUrl === "string" && /^https:\/\//i.test(fallbackUrl)) {
        // A tartalékot NEM tesszük a gyorsítótárba: nem blob-cím, nincs
        // mit felszabadítani, és a következő próbálkozás újra a
        // hitelesített utat kísérelje meg.
        return fallbackUrl;
      }
      return null;
    } finally {
      folyamatban.delete(path);
    }
  })();

  folyamatban.set(path, igeret);
  return igeret;
}

// A hitelesített letöltés felső határa (2026-10-15, valós hibából): CORS-hiba esetén (pl. új tartomány, ami még
// nincs a storage-cors.json-ban) az SDK hálózati hibának veszi, és akár 2 PERCIG újrapróbálja - addig a kép helyén
// csak a felirat látszott. Ennyi idő után a tartalék (nyilvános) címre váltunk.
export const LETOLTES_IDOKORLAT_MS = 6000;

/** A valódi letöltés: a Storage SDK a bejelentkezett tag nevében kéri le, időkorláttal. */
async function alapLetoltes(path) {
  const { getStorage, ref, getBlob } = await import("firebase/storage");
  const { getFirebaseApp } = await import("./firebaseClient.js");
  let idozito;
  const korlat = new Promise((_, reject) => {
    idozito = setTimeout(() => reject(Object.assign(new Error("időtúllépés"), { code: "app/kep-idotullepes" })), LETOLTES_IDOKORLAT_MS);
  });
  try {
    return await Promise.race([getBlob(ref(getStorage(getFirebaseApp()), path)), korlat]);
  } finally {
    clearTimeout(idozito);
  }
}

/**
 * Minden blob-cím elengedése. Kijelentkezéskor hívjuk: a képek az előző
 * felhasználóhoz tartoztak, és a memóriát sem tartjuk fogva feleslegesen.
 */
export function uritsdAKepeket() {
  for (const { url } of cache.values()) elenged(url);
  cache.clear();
  folyamatban.clear();
}

/** Csak a teszteknek: a gyorsítótár jelenlegi mérete. */
export function cacheMeret() {
  return cache.size;
}
